import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Radio, RadioGroup } from '@ds';
import { ORGAN_TYPES } from '@features/components/CaseFormBlocks/config';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { memo, useCallback } from 'react';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { CaseStatus, Laterality, OrganType } from '@/api/apis/case';
import { shouldUncheckOpoRequest } from '../utils';

type OrganTypeSelectorProps = {
  disabled?: boolean;
  isRequired?: boolean;
  hasPermission?: boolean;
  caseStatus?: CaseStatus;
  disabledOptions?: OrganType[];
  availableOrganTypes?: OrganType[];
};

export const OrganTypeSelector = memo(
  ({
    disabled,
    isRequired,
    hasPermission,
    caseStatus,
    disabledOptions,
    availableOrganTypes,
  }: OrganTypeSelectorProps) => {
    const { setValue, trigger, control } = useFormContext<CaseFormType>();
    const txCenterTrialPrograms = useWatch({ control, name: 'txCenterTrialPrograms' });

    const handleOrganChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const organ = e.target.value as OrganType;
        setValue('organType', organ);
        setValue('reAllocationCaseId', null);
        trigger('reAllocationCase');
        if (organ !== OrganType.Kidney) {
          setValue('laterality', Laterality.NotDefined);
        }
        if (shouldUncheckOpoRequest(caseStatus, organ, txCenterTrialPrograms)) setValue('opoRequest', false);
      },
      [setValue, trigger, caseStatus, txCenterTrialPrograms],
    );

    return (
      <FormRow title="Organ" isRequired={isRequired}>
        <Controller
          name="organType"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              isDisabled={disabled || !hasPermission}
              className="gap-8"
              orientation="horizontal"
              value={value || ''}
              isInvalid={invalid}
              errorMessage={error?.message}
              onChange={handleOrganChange}
              {...field}
            >
              {ORGAN_TYPES.filter((organ) => !availableOrganTypes || availableOrganTypes.includes(organ.value)).map(
                (organ) => (
                  <Radio
                    className="w-[100px] max-w-none"
                    key={organ.value}
                    value={organ.value as string}
                    size="lg"
                    isDisabled={disabledOptions?.includes(organ.value) ? true : undefined}
                  >
                    {organ.name}
                  </Radio>
                ),
              )}
            </RadioGroup>
          )}
        />
      </FormRow>
    );
  },
);
